import { useOnboardedEffect } from '@peanuds/app-shell';
import type { TransactionDetailsDialogData } from '@peanuds/modules';
import { normalizeIban } from '@peanuds/shared';
import { useBeneficiariesStore, useUserAccountsStore } from '@peanuds/shared-state';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { FLOW_KEYS, SEND_MONEY_FLOW_START_STEP } from '../internal/send-money.constants';
import {
  canRepeatOutgoingPayment,
  getRepeatPaymentBeneficiaryNameCandidates,
  getRepeatPaymentDestinationMatchKeys,
  mapTransactionDetailsToRepeatPaymentPrefill,
  toRepeatPaymentOwnAccounts,
} from './repeat-payment.mapper';
import { useRepeatPaymentState } from './repeat-payment.state';
import type {
  RepeatPaymentBeneficiaryMatch,
  RepeatPaymentDestinationMatchKeys,
} from './repeat-payment.types';

type CandidateBankAccount = {
  id?: string | null;
  iban?: string | null;
  accountNumber?: string | null;
  routingCodes?: Record<string, string> | null;
};

const normalizeBeneficiaryName = (name: string): string => {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
};

const normalizeAccountNumber = (accountNumber: string | null | undefined): string => {
  return accountNumber?.replace(/\s+/g, '') ?? '';
};

const toBeneficiaryMatch = (
  beneficiaryId: string,
  bankAccount: CandidateBankAccount,
): RepeatPaymentBeneficiaryMatch | null => {
  const beneficiaryBankAccountId = bankAccount.id?.trim() ?? '';
  if (beneficiaryBankAccountId.length === 0) {
    return null;
  }

  const iban = normalizeIban(bankAccount.iban ?? '');
  const accountNumber = normalizeAccountNumber(bankAccount.accountNumber);

  return {
    beneficiaryId,
    beneficiaryBankAccountId,
    iban: iban.length > 0 ? iban : null,
    accountNumber: accountNumber.length > 0 ? accountNumber : null,
    routingCodes: bankAccount.routingCodes ?? null,
  };
};

export const useRepeatPaymentNavigation = () => {
  const router = useRouter();
  const beneficiariesStore = useBeneficiariesStore();
  const userAccountsStore = useUserAccountsStore();
  const { bankAccountsByBeneficiary, beneficiaries, beneficiariesResponse } =
    storeToRefs(beneficiariesStore);
  const { accounts, hasLoadedAccounts } = storeToRefs(userAccountsStore);
  const { setPendingRepeatPaymentPrefill, clearPendingRepeatPaymentPrefill } =
    useRepeatPaymentState();

  const ensureBeneficiariesLoaded = async (): Promise<void> => {
    if (beneficiariesResponse.value === undefined) {
      await beneficiariesStore.fetchBeneficiaries();
    }
  };

  useOnboardedEffect(() =>
    Promise.all([userAccountsStore.ensureAccountsLoaded(), ensureBeneficiariesLoaded()]),
  );

  const ownAccounts = computed(() =>
    toRepeatPaymentOwnAccounts(accounts.value, hasLoadedAccounts.value),
  );

  const savedBeneficiaryIds = computed(() => {
    if (beneficiariesResponse.value === undefined) {
      return null;
    }

    return new Set(
      beneficiaries.value.flatMap((beneficiary) =>
        typeof beneficiary.id === 'string' ? [beneficiary.id] : [],
      ),
    );
  });

  const canRepeatPayment = (details: TransactionDetailsDialogData | null): boolean => {
    return canRepeatOutgoingPayment(details, ownAccounts.value, savedBeneficiaryIds.value);
  };

  const toBeneficiaryMatches = (beneficiaryIds: string[]): RepeatPaymentBeneficiaryMatch[] => {
    return beneficiaryIds.flatMap((beneficiaryId) =>
      (bankAccountsByBeneficiary.value[beneficiaryId] ?? []).flatMap((bankAccount) => {
        const match = toBeneficiaryMatch(beneficiaryId, bankAccount);
        return match === null ? [] : [match];
      }),
    );
  };

  const findBeneficiaryMatch = (
    beneficiaryIds: string[],
    {
      beneficiaryBankAccountId,
      normalizedIban,
      normalizedAccountNumber,
    }: RepeatPaymentDestinationMatchKeys,
    allowAccountNumber = true,
  ): RepeatPaymentBeneficiaryMatch | null => {
    const matches = toBeneficiaryMatches(beneficiaryIds);
    const accountNumberMatches = allowAccountNumber
      ? matches.filter(
          (match) =>
            match.accountNumber !== null && match.accountNumber === normalizedAccountNumber,
        )
      : [];

    return (
      matches.find((match) => match.beneficiaryBankAccountId === beneficiaryBankAccountId) ??
      matches.find((match) => match.iban !== null && match.iban === normalizedIban) ??
      (accountNumberMatches.length === 1 ? accountNumberMatches[0] : undefined) ??
      null
    );
  };

  const resolveRepeatPaymentBeneficiary = async (
    details: TransactionDetailsDialogData,
  ): Promise<RepeatPaymentBeneficiaryMatch | null> => {
    const matchKeys = getRepeatPaymentDestinationMatchKeys(details);
    if (matchKeys === null) {
      return null;
    }

    if (beneficiaries.value.length === 0) {
      await beneficiariesStore.fetchBeneficiaries();
    }

    if (matchKeys.beneficiaryId !== null) {
      const beneficiaryId = matchKeys.beneficiaryId;
      if (
        matchKeys.beneficiaryBankAccountId === null ||
        !beneficiaries.value.some((beneficiary) => beneficiary.id === beneficiaryId)
      ) {
        return null;
      }

      const didLoad = await beneficiariesStore.fetchBankAccountsForBeneficiaryId({ beneficiaryId });
      if (!didLoad) return null;
      return (
        toBeneficiaryMatches([beneficiaryId]).find(
          (match) => match.beneficiaryBankAccountId === matchKeys.beneficiaryBankAccountId,
        ) ?? null
      );
    }

    const beneficiaryNames = new Set(
      getRepeatPaymentBeneficiaryNameCandidates(details).map(normalizeBeneficiaryName),
    );

    const candidateBeneficiaryIds = beneficiaries.value.flatMap((beneficiary) => {
      if (typeof beneficiary.id !== 'string' || typeof beneficiary.fullName !== 'string') {
        return [];
      }

      return beneficiaryNames.has(normalizeBeneficiaryName(beneficiary.fullName))
        ? [beneficiary.id]
        : [];
    });

    const cachedMatch = findBeneficiaryMatch(candidateBeneficiaryIds, matchKeys);
    if (cachedMatch !== null) {
      return cachedMatch;
    }

    await Promise.allSettled(
      candidateBeneficiaryIds.map((beneficiaryId) =>
        beneficiariesStore.fetchBankAccountsForBeneficiaryId({ beneficiaryId }),
      ),
    );

    const namedMatch = findBeneficiaryMatch(candidateBeneficiaryIds, matchKeys);
    if (namedMatch !== null) {
      return namedMatch;
    }

    const allBeneficiaryIds = beneficiaries.value.flatMap((beneficiary) =>
      typeof beneficiary.id === 'string' ? [beneficiary.id] : [],
    );

    const cachedIbanMatch = findBeneficiaryMatch(allBeneficiaryIds, matchKeys, false);
    if (cachedIbanMatch !== null) {
      return cachedIbanMatch;
    }

    if (matchKeys.beneficiaryBankAccountId === null && matchKeys.normalizedIban === null) {
      return null;
    }

    await beneficiariesStore.fetchAllBankAccounts();

    return findBeneficiaryMatch(allBeneficiaryIds, matchKeys, false);
  };

  const startRepeatPayment = async (details: TransactionDetailsDialogData): Promise<boolean> => {
    try {
      if (!canRepeatPayment(details)) {
        clearPendingRepeatPaymentPrefill();
        return false;
      }

      const match = await resolveRepeatPaymentBeneficiary(details);
      if (match === null) {
        clearPendingRepeatPaymentPrefill();
        return false;
      }

      const prefill = mapTransactionDetailsToRepeatPaymentPrefill(details, match);
      if (prefill === null) {
        clearPendingRepeatPaymentPrefill();
        return false;
      }

      setPendingRepeatPaymentPrefill(prefill);

      await router.push({
        name: FLOW_KEYS['existing-contact'],
        params: { step: SEND_MONEY_FLOW_START_STEP },
      });

      return true;
    } catch {
      clearPendingRepeatPaymentPrefill();
      return false;
    }
  };

  return {
    canRepeatPayment,
    startRepeatPayment,
  };
};
